import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { erpSyncApi, schedulerApi } from '../services/api';
import {
  Database, Mail, HardDrive, Webhook, Cloud, Settings2,
  Plus, Trash2, RefreshCw, Clock, AlertTriangle, CheckCircle2, XCircle, Play, Pause,
} from 'lucide-react';

const METODO_LABEL = {
  email: { label: 'Email CSV', icon: Mail, color: 'bg-blue-100 text-blue-700' },
  sftp: { label: 'SFTP', icon: HardDrive, color: 'bg-violet-100 text-violet-700' },
  webhook: { label: 'Webhook API', icon: Webhook, color: 'bg-emerald-100 text-emerald-700' },
  onedrive: { label: 'OneDrive', icon: Cloud, color: 'bg-cyan-100 text-cyan-700' },
  gdrive: { label: 'Google Drive', icon: Cloud, color: 'bg-red-100 text-red-700' },
  api_erp: { label: 'API ERP', icon: Database, color: 'bg-amber-100 text-amber-700' },
};

const TABS = ['config', 'historial', 'cola', 'scheduler'];

export default function IntegracionesERP() {
  const [tab, setTab] = useState('config');
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [cola, setCola] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nuevaConfig, setNuevaConfig] = useState(null);
  const [apiKeyAviso, setApiKeyAviso] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [c, l, q, s] = await Promise.all([
        erpSyncApi.listConfigs(),
        erpSyncApi.logs(50),
        erpSyncApi.colaRevision({ estado: 'pendiente' }),
        schedulerApi.list(),
      ]);
      setConfigs(c.items || []);
      setLogs(l.items || []);
      setCola(q.items || []);
      setJobs(s.jobs || []);
      setSchedulerRunning(!!s.running);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const crearConfig = async (data) => {
    try {
      const res = await erpSyncApi.createConfig(data);
      if (res.webhook_api_key) {
        setApiKeyAviso({ key: res.webhook_api_key, nombre: data.nombre });
      }
      setNuevaConfig(null);
      await cargar();
    } catch (e) {
      alert(e.message);
    }
  };

  const eliminarConfig = async (id) => {
    if (!window.confirm('¿Eliminar esta configuración?')) return;
    await erpSyncApi.deleteConfig(id);
    await cargar();
  };

  const toggleActivoConfig = async (cfg) => {
    await erpSyncApi.updateConfig(cfg.id, { activo: !cfg.activo });
    await cargar();
  };

  const toggleJob = async (job) => {
    await schedulerApi.update(job.job_id, { activo: !job.activo, trigger: job.trigger });
    await cargar();
  };

  return (
    <div className="space-y-6" data-testid="integraciones-erp">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900 flex items-center gap-3">
            <Database className="w-7 h-7 text-brand-500" />
            Integraciones ERP
          </h1>
          <p className="text-ink-500 mt-1">Sincronización automática de pedidos y albaranes desde tu ERP.</p>
        </div>
        <Button variant="outline" onClick={cargar} data-testid="btn-recargar-erp">
          <RefreshCw className="w-4 h-4 mr-2" /> Recargar
        </Button>
      </div>

      {apiKeyAviso && (
        <Card className="p-4 border-amber-300 bg-amber-50" data-testid="apikey-aviso">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900 mb-1">API Key generada — guárdala AHORA</div>
              <div className="text-sm text-amber-800 mb-2">Esta clave no volverá a mostrarse. Configúrala en el ERP para que pueda enviar datos a <code className="bg-white px-1 rounded">POST /api/erp/sync</code> con la cabecera <code className="bg-white px-1 rounded">X-ERP-API-Key</code>.</div>
              <pre className="bg-white border border-amber-200 rounded p-2 text-xs break-all font-mono">{apiKeyAviso.key}</pre>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => setApiKeyAviso(null)}>
                Lo he guardado
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="flex gap-2 border-b border-ink-200">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              tab === t ? 'border-brand-500 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}
            data-testid={`tab-erp-${t}`}
          >
            {t === 'config' && 'Configuración'}
            {t === 'historial' && `Historial (${logs.length})`}
            {t === 'cola' && `Cola revisión (${cola.length})`}
            {t === 'scheduler' && `Scheduler (${jobs.filter((j) => j.activo).length}/${jobs.length})`}
          </button>
        ))}
      </div>

      {tab === 'config' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setNuevaConfig({ metodo: 'email', nombre: '', activo: true })} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-nueva-config-erp">
              <Plus className="w-4 h-4 mr-2" /> Nueva configuración
            </Button>
          </div>

          {loading && <div className="text-ink-500 text-sm">Cargando…</div>}
          {!loading && configs.length === 0 && (
            <Card className="p-8 text-center">
              <Settings2 className="w-10 h-10 mx-auto text-ink-300 mb-3" />
              <p className="text-ink-500 mb-4">No hay integraciones configuradas.</p>
              <p className="text-sm text-ink-400">Crea una configuración tipo Email o SFTP para empezar a recibir datos del ERP automáticamente.</p>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {configs.map((cfg) => {
              const meta = METODO_LABEL[cfg.metodo] || { label: cfg.metodo, icon: Database, color: 'bg-ink-100 text-ink-700' };
              const Icon = meta.icon;
              return (
                <Card key={cfg.id} className="p-4" data-testid={`config-card-${cfg.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${meta.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-ink-900">{cfg.nombre}</div>
                        <div className="text-xs text-ink-500">{meta.label}</div>
                      </div>
                    </div>
                    <Badge className={cfg.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-500'}>
                      {cfg.activo ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </div>
                  <div className="text-xs text-ink-600 space-y-1 mb-3">
                    {cfg.email_buzon && <div>📬 Buzón: <code className="bg-ink-50 px-1 rounded">{cfg.email_buzon}</code></div>}
                    {cfg.sftp_host && <div>📂 SFTP: <code className="bg-ink-50 px-1 rounded">{cfg.sftp_usuario}@{cfg.sftp_host}:{cfg.sftp_puerto || 22}</code></div>}
                    {cfg.ultima_sync && <div>⏱ Última sync: {new Date(cfg.ultima_sync).toLocaleString()}</div>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggleActivoConfig(cfg)} data-testid={`btn-toggle-${cfg.id}`}>
                      {cfg.activo ? <><Pause className="w-3.5 h-3.5 mr-1" /> Desactivar</> : <><Play className="w-3.5 h-3.5 mr-1" /> Activar</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => eliminarConfig(cfg.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`btn-eliminar-${cfg.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          {nuevaConfig && (
            <ConfigForm
              initial={nuevaConfig}
              onSave={crearConfig}
              onCancel={() => setNuevaConfig(null)}
            />
          )}
        </div>
      )}

      {tab === 'historial' && (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left">
              <tr>
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Tipo</th>
                <th className="px-4 py-2">Archivo</th>
                <th className="px-4 py-2">Nuevos</th>
                <th className="px-4 py-2">Actualizados</th>
                <th className="px-4 py-2">Errores</th>
                <th className="px-4 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-ink-400">Sin ejecuciones todavía.</td></tr>
              )}
              {logs.map((log, idx) => (
                <tr key={idx} className={idx % 2 ? 'bg-ink-50/40' : ''} data-testid={`log-row-${idx}`}>
                  <td className="px-4 py-2 text-xs">{new Date(log.fecha_ejecucion).toLocaleString()}</td>
                  <td className="px-4 py-2">{log.tipo_datos}</td>
                  <td className="px-4 py-2 truncate max-w-xs">{log.archivo_procesado}</td>
                  <td className="px-4 py-2 text-emerald-700 font-medium">{log.registros_nuevos}</td>
                  <td className="px-4 py-2 text-blue-700 font-medium">{log.registros_actualizados}</td>
                  <td className="px-4 py-2 text-rose-700 font-medium">{log.registros_error}</td>
                  <td className="px-4 py-2">
                    {log.estado === 'ok' && <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1 inline" /> OK</Badge>}
                    {log.estado === 'ok_con_warnings' && <Badge className="bg-amber-100 text-amber-700">warnings</Badge>}
                    {log.estado === 'error' && <Badge className="bg-rose-100 text-rose-700"><XCircle className="w-3 h-3 mr-1 inline" /> Error</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'cola' && (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left">
              <tr>
                <th className="px-4 py-2">Tipo</th>
                <th className="px-4 py-2">Valor</th>
                <th className="px-4 py-2">Archivo origen</th>
                <th className="px-4 py-2">Detectado</th>
                <th className="px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cola.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-400">Sin ítems pendientes de revisar.</td></tr>
              )}
              {cola.map((item) => (
                <tr key={item.id} data-testid={`cola-row-${item.id}`}>
                  <td className="px-4 py-2">
                    {item.tipo === 'cif_no_encontrado' ? (
                      <Badge className="bg-amber-100 text-amber-700">CIF no encontrado</Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-700">Ref. sin mapeo</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{item.valor}</td>
                  <td className="px-4 py-2 text-xs text-ink-500">{item.contexto?.archivo}</td>
                  <td className="px-4 py-2 text-xs">{new Date(item.fecha_deteccion).toLocaleString()}</td>
                  <td className="px-4 py-2">
                    <Button size="sm" variant="outline" disabled title="Próximamente en Sprint S11" data-testid={`btn-asignar-${item.id}`}>
                      Asignar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'scheduler' && (
        <>
          <Card className="p-4 bg-brand-50/40 border-brand-200">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-brand-600" />
              <div className="text-sm text-ink-700">
                Estado scheduler: {schedulerRunning ? (
                  <Badge className="bg-emerald-100 text-emerald-700">Activo</Badge>
                ) : (
                  <Badge className="bg-ink-100 text-ink-500">Detenido (dev)</Badge>
                )}
                <span className="ml-3 text-xs text-ink-500">Para activarlo en producción: <code className="bg-white px-1 rounded">SCHEDULER_ENABLED=true</code> en el .env</span>
              </div>
            </div>
          </Card>

          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left">
                <tr>
                  <th className="px-4 py-2">Job</th>
                  <th className="px-4 py-2">Descripción</th>
                  <th className="px-4 py-2">Trigger</th>
                  <th className="px-4 py-2">Activo</th>
                  <th className="px-4 py-2">Próxima ejecución</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.job_id} data-testid={`job-row-${job.job_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{job.job_id}</td>
                    <td className="px-4 py-2 text-xs">{job.descripcion}</td>
                    <td className="px-4 py-2 text-xs text-ink-500">
                      {job.trigger?.type === 'interval' && `cada ${job.trigger.minutes || 0} min`}
                      {job.trigger?.type === 'cron' && `cron ${JSON.stringify(job.trigger)}`}
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        size="sm"
                        variant={job.activo ? 'default' : 'outline'}
                        className={job.activo ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}
                        onClick={() => toggleJob(job)}
                        data-testid={`btn-job-toggle-${job.job_id}`}
                      >
                        {job.activo ? 'ON' : 'OFF'}
                      </Button>
                    </td>
                    <td className="px-4 py-2 text-xs text-ink-500">{job.proxima_ejecucion || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function ConfigForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    nombre: initial.nombre || '',
    metodo: initial.metodo || 'email',
    activo: true,
    email_buzon: '',
    sftp_host: '',
    sftp_puerto: 22,
    sftp_usuario: '',
    sftp_directorio: '/uploads/erp_sync/pending/',
    mapeo_columnas_pedidos: {},
    mapeo_columnas_albaranes: {},
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nombre) { alert('Nombre requerido'); return; }
    onSave(form);
  };

  return (
    <Card className="p-5" data-testid="form-config-erp">
      <h3 className="font-semibold text-ink-900 mb-4">Nueva integración</h3>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-ink-700 block mb-1">Nombre descriptivo</label>
          <input
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            className="w-full border border-ink-200 rounded px-3 py-2 text-sm"
            placeholder="ERP Principal"
            data-testid="input-nombre-config"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-ink-700 block mb-1">Método</label>
          <select
            value={form.metodo}
            onChange={(e) => setForm({ ...form, metodo: e.target.value })}
            className="w-full border border-ink-200 rounded px-3 py-2 text-sm"
            data-testid="select-metodo-config"
          >
            {Object.entries(METODO_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {form.metodo === 'email' && (
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-ink-700 block mb-1">Buzón de correo dedicado</label>
            <input
              value={form.email_buzon}
              onChange={(e) => setForm({ ...form, email_buzon: e.target.value })}
              className="w-full border border-ink-200 rounded px-3 py-2 text-sm"
              placeholder="erp-datos@crm.tudominio.com"
              data-testid="input-email-buzon"
            />
            <p className="text-xs text-ink-500 mt-1">Configura esta dirección como destino de exportación automática en tu ERP.</p>
          </div>
        )}

        {form.metodo === 'sftp' && (
          <>
            <div>
              <label className="text-sm font-medium text-ink-700 block mb-1">Host</label>
              <input value={form.sftp_host} onChange={(e) => setForm({ ...form, sftp_host: e.target.value })} className="w-full border border-ink-200 rounded px-3 py-2 text-sm" placeholder="sftp.tudominio.com" data-testid="input-sftp-host" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700 block mb-1">Puerto</label>
              <input type="number" value={form.sftp_puerto} onChange={(e) => setForm({ ...form, sftp_puerto: parseInt(e.target.value, 10) })} className="w-full border border-ink-200 rounded px-3 py-2 text-sm" data-testid="input-sftp-puerto" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700 block mb-1">Usuario</label>
              <input value={form.sftp_usuario} onChange={(e) => setForm({ ...form, sftp_usuario: e.target.value })} className="w-full border border-ink-200 rounded px-3 py-2 text-sm" data-testid="input-sftp-usuario" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700 block mb-1">Directorio pending</label>
              <input value={form.sftp_directorio} onChange={(e) => setForm({ ...form, sftp_directorio: e.target.value })} className="w-full border border-ink-200 rounded px-3 py-2 text-sm font-mono text-xs" data-testid="input-sftp-dir" />
            </div>
          </>
        )}

        {form.metodo === 'webhook' && (
          <div className="md:col-span-2 bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
            Al crear esta integración, se generará una <strong>API Key</strong> que deberás copiar y configurar en tu ERP. Solo se mostrará una vez.
          </div>
        )}

        <div className="md:col-span-2 flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} data-testid="btn-cancelar-config">Cancelar</Button>
          <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-config">Crear</Button>
        </div>
      </form>
    </Card>
  );
}
